import { degreesToRadians, point, polygon, toMercator, toWgs84 } from '@turf/turf';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { Feature, Polygon, Position } from 'geojson';
import { mat4, vec3 } from 'gl-matrix';

import { Target } from '@/types/c2/c2.types';
import { FeatureTypeEnum } from '@/types/map.types';
import { SpatialLocation } from '@/types/sensor/sensor.types';
import { JammingModule } from '@/types/sensor/sensors';

function getRotationMatrix4(yawRad: number, pitchRad: number, rollRad: number): mat4 {
  const M = mat4.create();
  mat4.rotateZ(M, M, yawRad);
  mat4.rotateX(M, M, pitchRad);
  mat4.rotateY(M, M, rollRad);
  return M;
}

export function coneFootprintOnPlan(
  origin: Position,
  height: number,
  targetHeight: number,
  range: number,
  rawYawRad: number,
  pitchRad: number,
  rollRad: number,
  halfAperture: number,
  segments = 64,
): Position[] | null {
  const yawRad = -rawYawRad;

  const apexMerc = toMercator(point(origin)).geometry.coordinates;
  const M4 = getRotationMatrix4(yawRad, pitchRad, rollRad);

  const axis = vec3.create();
  vec3.transformMat4(axis, vec3.fromValues(0, 0, -1), M4);
  vec3.normalize(axis, axis);

  const dz = targetHeight - height;
  if (axis[2] * dz <= 0) {
    // Middle axis of cone projection does not intersect with plan
    return null;
  }

  const tmp = Math.abs(axis[0]) < 0.9 ? vec3.fromValues(1, 0, 0) : vec3.fromValues(0, 1, 0);
  const e1 = vec3.create();
  vec3.cross(e1, axis, tmp);
  vec3.normalize(e1, e1);
  const e2 = vec3.create();
  vec3.cross(e2, axis, e1);

  const ring: Position[] = [];
  const sinTh = Math.sin(halfAperture);
  const cosTh = Math.cos(halfAperture);

  for (let i = 0; i < segments; i++) {
    const phi = (2 * Math.PI * i) / segments;
    const dir = vec3.create();
    vec3.scale(dir, axis, cosTh);
    const circ = vec3.create();
    vec3.scale(circ, e1, sinTh * Math.cos(phi));
    vec3.scaleAndAdd(circ, circ, e2, sinTh * Math.sin(phi));
    vec3.add(dir, dir, circ);

    // intersection z=0
    const dirZ = dir[2];
    if (Math.abs(dirZ) < 1e-6) continue;
    const s = dz / dirZ;
    if (s <= 0 || s > range) continue;

    const x = apexMerc[0] + dir[0] * s;
    const y = apexMerc[1] + dir[1] * s;
    const [lon, lat] = toWgs84(point([x, y])).geometry.coordinates;
    ring.push([lon, lat]);
  }

  if (ring.length > 2) {
    ring.push(ring[0]);
    return ring;
  }
  return null;
}

function computeIntersection(
  location: SpatialLocation,
  intersectionHeight: number,
  jammingModule: JammingModule,
): Feature<Polygon> | null {
  const origin = toLonLatArray(location.position);
  const pitchRad = degreesToRadians(location.pitch ?? 0);
  const height = location.height ?? 0;
  const yawRad = degreesToRadians(location.heading ?? 0);
  const rollRad = degreesToRadians(location.roll ?? 0);
  const semiApertureRad = degreesToRadians(jammingModule.aperture / 2);
  const range = jammingModule.range;

  if (!origin || height <= 0) {
    return null;
  }

  const coords = coneFootprintOnPlan(
    origin,
    height,
    intersectionHeight,
    range,
    yawRad,
    pitchRad,
    rollRad,
    semiApertureRad,
    36,
  );
  if (coords !== null) {
    return polygon([coords]);
  }
  return null;
}

function transformFeature(feature: Feature<Polygon> | null, id: string, color: string): Feature<Polygon> | null {
  if (feature == null) {
    return null;
  }
  return {
    type: 'Feature',
    id: `${FeatureTypeEnum.TARGET_GCS_JAMMING_AREA}-${id}`,
    properties: {
      id,
      featureType: FeatureTypeEnum.TARGET_GCS_JAMMING_AREA,
      color,
    },
    geometry: {
      type: 'Polygon',
      coordinates: feature.geometry.coordinates,
    },
  };
}

export const toJammingAreaFeatures = (
  dronesWithJammingInfo: {
    drone: Target<SpatialLocation>;
    jammingTarget: Target<SpatialLocation> | null;
    jammingModule: JammingModule;
  }[],
): Feature[] =>
  dronesWithJammingInfo.flatMap((dronesWithJammingModule) => {
    const drone = dronesWithJammingModule.drone;
    const jammingModule = dronesWithJammingModule.jammingModule;
    const jammingTargetHeight = dronesWithJammingModule.jammingTarget?.lastPosition.location?.height ?? null;
    const groundFootprint = computeIntersection(drone.lastPosition.location!, 0, jammingModule);
    const targetFootprint =
      (jammingTargetHeight !== null &&
        computeIntersection(drone.lastPosition.location!, jammingTargetHeight, jammingModule)) ||
      null;
    return [
      transformFeature(groundFootprint, drone.id, 'yellow'),
      transformFeature(targetFootprint, drone.id, 'red'),
    ].filter(Boolean) as Feature[];
  });
